import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Search, Music, Trash2, Edit3, Plus, Loader2, ChevronDown, ChevronUp, Check, Link2, Clock, ShieldCheck, AlertTriangle, CheckCircle, RefreshCw, ExternalLink, History, Flag, RotateCcw, EyeOff } from 'lucide-react';
import {
  flagSongLink,
  getMyLibrarySongs,
  getMyLinkEditingRevoked,
  getSongLengths,
  getSongLinkHistory,
  removeLibrarySongs,
  resolveSpotifyLink,
  revertSongLink,
  saveSongLinks,
  firstFailedSave,
  searchLibrarySongs,
  type IntegrityStatus,
  type LibraryHistoryEntry,
  type LibraryHistoryAction,
  type LibraryLink,
  type LibrarySong,
  type LinkProvider,
  type SongLengthSource,
} from '../../services/songMatchMemory';
import { SongLinkHistoryList } from '../../components/dj/SongLinkHistoryList';
import { bigLengthGaps, confidenceText, formatLengthMs } from '../../utils/linkConfidence';
import { propagateMatchFix } from '../../services/requests';
import { checkAndPersistIntegrity, batchCheckIntegrity, type BatchCheckItem } from '../../services/matchIntegrity';
import { resolveFromUrl } from '../../services/odesliApi';
import { playModeConfig, searchUrlFor, type PlayMode } from '../../services/playModes';
import { cleanPastedLink, platformNameOf } from '../../utils/songLinks';
import { useAuth } from '../../context/AuthContext';
import { flagFor, getSongRecognitions, setBridgeRecognitionFlag, type SongRecognition } from '../../services/songRecognition';
import { deleteBridgeAlias, getMyBridgeAliases, type BridgeAlias } from '../../services/bridgeAliases';
import { getMySongTags, setSongTags } from '../../services/songTags';
import { tagCounts } from '../../utils/songTags';
import { SongTagEditor } from '../../components/dj/SongTagEditor';
import toast from 'react-hot-toast';

type SortField = 'title' | 'artist' | 'recent';
type IntegrityFilter = 'all' | 'mismatch' | 'verified' | 'unchecked' | 'error' | 'matched' | 'reported';

const PROVIDERS: LinkProvider[] = ['spotify', 'tidal', 'apple'];
const MODE_BY_PROVIDER: Record<LinkProvider, PlayMode> = {
  spotify: 'spotify_manual',
  tidal: 'tidal_manual',
  apple: 'apple_manual',
};

const REVOKED_HINT = 'Link editing is paused on your account. You can add links for new songs; changes to existing links go to the owner.';
const REINSTATABLE: LibraryHistoryAction[] = ['create', 'replace', 'migrate', 'revert', 'review_approved'];
const LENGTH_LABELS: Record<SongLengthSource, string> = { apple: 'Apple', spotify: 'Spotify', tidal: 'TIDAL' };
const MAX_FLAG_REASON = 500;

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function formatDurationMmSs(ms: number): string {
  if (!ms) return '';
  const totalSecs = Math.floor(ms / 1000);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatSavedDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}

function savedLinks(song: LibrarySong): Array<[LinkProvider, LibraryLink]> {
  return PROVIDERS.flatMap(provider => {
    const link = song.links[provider];
    return link ? [[provider, link] as [LinkProvider, LibraryLink]] : [];
  });
}

function statusOf(link: LibraryLink): IntegrityStatus {
  return link.integrityStatus || 'unchecked';
}

function songStatus(song: LibrarySong): IntegrityStatus {
  const statuses = savedLinks(song).map(([, link]) => statusOf(link));
  for (const status of ['mismatch', 'error', 'unchecked', 'matched', 'verified'] as IntegrityStatus[]) {
    if (statuses.includes(status)) return status;
  }
  return 'unchecked';
}

function openSearch(url: string) {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    window.open(url, '_blank', 'noopener');
  } else {
    window.location.href = url;
  }
}

export default function MatchLibraryPage() {
  const { user } = useAuth();
  const [songs, setSongs] = useState<LibrarySong[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('recent');
  const [expandedSong, setExpandedSong] = useState<string | null>(null);
  // Replace / add link form, keyed "songKey|provider"
  const [editingLink, setEditingLink] = useState<string | null>(null);
  const [replaceUrl, setReplaceUrl] = useState('');
  const [replaceSaving, setReplaceSaving] = useState(false);
  const [removingSong, setRemovingSong] = useState<string | null>(null);
  // Integrity check state
  const [integrityFilter, setIntegrityFilter] = useState<IntegrityFilter>('all');
  const [batchChecking, setBatchChecking] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ checked: 0, total: 0, current: '' });
  const batchAbortRef = useRef<AbortController | null>(null);
  const [recheckingLink, setRecheckingLink] = useState<string | null>(null);
  // Trust & review (ITER-139)
  const [historyBySong, setHistoryBySong] = useState<Record<string, LibraryHistoryEntry[]>>({});
  const [openHistory, setOpenHistory] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState<string | null>(null);
  const [historyAction, setHistoryAction] = useState<string | null>(null);
  const [flagForm, setFlagForm] = useState<{ entryId: string; reason: string } | null>(null);
  const [editingRevoked, setEditingRevoked] = useState(false);
  const [songLengths, setSongLengths] = useState<Map<string, Partial<Record<SongLengthSource, number>>>>(new Map());
  const [recognitions, setRecognitions] = useState<Map<string, SongRecognition>>(new Map());
  const [unflagging, setUnflagging] = useState<string | null>(null);
  const [bridgeAliases, setBridgeAliases] = useState<BridgeAlias[]>([]);
  const [aliasesOpen, setAliasesOpen] = useState(true);
  const [removingAlias, setRemovingAlias] = useState<string | null>(null);
  const [tagsBySong, setTagsBySong] = useState<Map<string, string[]>>(new Map());
  const [tagFilter, setTagFilter] = useState('all');
  const [savingTagsFor, setSavingTagsFor] = useState<string | null>(null);

  const djUid = user?.uid || '';

  const loadSongs = useCallback(async () => {
    try {
      setSongs(await getMyLibrarySongs(djUid));
    } catch (err) {
      console.error('[MatchLibraryPage] Failed to load the link library:', err);
      toast.error('Failed to load match library');
    }
  }, [djUid]);

  useEffect(() => {
    if (!djUid) return;
    loadSongs().finally(() => setLoading(false));
    getMyLinkEditingRevoked(djUid).then(setEditingRevoked);
    getMySongTags(djUid)
      .then(setTagsBySong)
      .catch(err => {
        console.error('[MatchLibraryPage] Failed to load song tags:', err);
        toast.error('Failed to load tags');
      });
  }, [djUid, loadSongs]);

  const tagSummary = useMemo(
    () => tagCounts(tagsBySong, new Set(songs.map(song => song.key))),
    [tagsBySong, songs],
  );
  const allTags = useMemo(() => tagSummary.map(entry => entry.tag), [tagSummary]);

  const handleTagsChange = async (songKey: string, next: string[]) => {
    setSavingTagsFor(songKey);
    try {
      await setSongTags(djUid, songKey, next);
      setTagsBySong(prev => {
        const updated = new Map(prev);
        if (next.length === 0) updated.delete(songKey);
        else updated.set(songKey, next);
        return updated;
      });
    } catch (err) {
      console.error('[MatchLibraryPage] Saving song tags failed:', err);
      toast.error('Could not save tags');
    } finally {
      setSavingTagsFor(null);
    }
  };

  const loadBridgeAliases = useCallback(async () => {
    try {
      setBridgeAliases(await getMyBridgeAliases(djUid));
    } catch (err) {
      console.error('[MatchLibraryPage] Failed to load the versions Bridge has learned:', err);
      toast.error('Failed to load the versions Bridge has learned');
    }
  }, [djUid]);

  useEffect(() => {
    if (!djUid) return;
    loadBridgeAliases();
  }, [djUid, loadBridgeAliases]);

  const handleRemoveBridgeAlias = async (aliasId: string) => {
    setRemovingAlias(aliasId);
    try {
      await deleteBridgeAlias(djUid, aliasId);
      await loadBridgeAliases();
      toast.success('Bridge will ask again next time');
    } catch (err) {
      console.error('[MatchLibraryPage] Removing a learned version failed:', err);
      toast.error(`Couldn't remove it: ${err instanceof Error && err.message ? err.message : 'unknown error'}`);
    } finally {
      setRemovingAlias(null);
    }
  };

  const isrcsJoined = useMemo(
    () => [...new Set(songs.map(song => song.isrc).filter((isrc): isrc is string => Boolean(isrc)))].sort().join(','),
    [songs],
  );

  const songKeysJoined = useMemo(() => songs.map(song => song.key).sort().join(','), [songs]);

  useEffect(() => {
    if (!songKeysJoined) return;
    getSongRecognitions(songKeysJoined.split(','))
      .then(setRecognitions)
      .catch((err: unknown) => console.error('[MatchLibraryPage] Loading Bridge recognition flags failed:', err));
  }, [songKeysJoined]);

  const handleRemoveRecognitionFlag = async (songKey: string, provider: LinkProvider) => {
    const busyKey = `${songKey}|${provider}`;
    setUnflagging(busyKey);
    try {
      await setBridgeRecognitionFlag({ songKey, provider, flagged: false, source: 'library' });
      setRecognitions(await getSongRecognitions(songKeysJoined.split(',')));
      toast.success('Flag removed');
    } catch (err) {
      console.error('[MatchLibraryPage] Removing a Bridge recognition flag failed:', err);
      toast.error(`Couldn't remove the flag: ${err instanceof Error && err.message ? err.message : 'unknown error'}`);
    } finally {
      setUnflagging(null);
    }
  };

  useEffect(() => {
    if (!isrcsJoined) return;
    getSongLengths(isrcsJoined.split(','))
      .then(setSongLengths)
      .catch((err: unknown) => console.error('[MatchLibraryPage] Loading song lengths failed:', err));
  }, [isrcsJoined]);

  const loadHistory = useCallback(async (songKey: string) => {
    setHistoryLoading(songKey);
    try {
      const entries = await getSongLinkHistory(songKey);
      setHistoryBySong(prev => ({ ...prev, [songKey]: entries }));
    } catch (err) {
      console.error('[MatchLibraryPage] Loading link history failed:', err);
      toast.error('Failed to load the link history');
    } finally {
      setHistoryLoading(null);
    }
  }, []);

  const filteredSongs = useMemo(() => {
    let result = searchQuery.trim() ? searchLibrarySongs(songs, searchQuery) : [...songs];

    if (integrityFilter === 'reported') {
      result = result.filter(song => savedLinks(song).some(([, link]) => link.wrongSongReports > 0));
    } else if (integrityFilter !== 'all') {
      result = result.filter(song => savedLinks(song).some(([, link]) => statusOf(link) === integrityFilter));
    }

    if (tagFilter !== 'all') {
      result = result.filter(song => (tagsBySong.get(song.key) ?? []).includes(tagFilter));
    }

    result.sort((a, b) => {
      switch (sortField) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'artist':
          return a.artist.localeCompare(b.artist);
        case 'recent':
        default:
          return b.updatedAt.localeCompare(a.updatedAt);
      }
    });

    return result;
  }, [songs, searchQuery, sortField, integrityFilter, tagFilter, tagsBySong]);

  // Integrity stats across all saved links
  const integrityStats = useMemo(() => {
    let mismatches = 0;
    let unchecked = 0;
    let errors = 0;
    for (const song of songs) {
      for (const [, link] of savedLinks(song)) {
        const status = statusOf(link);
        if (status === 'mismatch') mismatches++;
        else if (status === 'unchecked') unchecked++;
        else if (status === 'error') errors++;
      }
    }
    return { mismatches, unchecked, errors };
  }, [songs]);

  const handleToggleExpand = (key: string) => {
    setExpandedSong(prev => prev === key ? null : key);
    setEditingLink(null);
  };

  const handleStartReplace = (song: LibrarySong, provider: LinkProvider) => {
    setEditingLink(`${song.key}|${provider}`);
    setReplaceUrl('');
  };

  const handleSaveReplace = useCallback(async (song: LibrarySong, provider: LinkProvider) => {
    if (!replaceUrl.trim()) return;
    setReplaceSaving(true);
    try {
      const cleaned = await cleanPastedLink(replaceUrl, resolveSpotifyLink);
      if (!cleaned.ok) {
        toast.error(cleaned.message);
        return;
      }
      if (cleaned.provider !== provider) {
        toast.error(`That's a ${cleaned.platformName} link. Paste a ${platformNameOf(provider)} link here.`);
        return;
      }

      let songLinkUrl: string | null = null;
      try {
        const resolved = await resolveFromUrl(cleaned.url);
        songLinkUrl = resolved?.songLinkUrl || null;
      } catch (err) {
        console.warn('[MatchLibraryPage] song.link lookup failed; saving without it:', err);
      }

      const existing = song.links[provider];
      const results = await saveSongLinks([{
        songKey: song.key,
        link: cleaned.url,
        songLinkUrl,
        albumArtUrl: existing?.albumArtUrl ?? null,
        durationMs: existing?.durationMs ?? null,
        durationVerified: existing?.durationVerified ?? null,
      }]);
      const failure = firstFailedSave(results);
      if (failure) {
        toast.error(failure);
        return;
      }
      if (results.some(result => result.ok && result.status === 'queued_for_review')) {
        setEditingLink(null);
        setReplaceUrl('');
        setEditingRevoked(true);
        toast('Your library edits are paused, so this change was sent to the owner for review.', { duration: 6000 });
        return;
      }

      const updatedCount = await propagateMatchFix(djUid, song.title, song.artist, cleaned.url, songLinkUrl);
      setEditingLink(null);
      setReplaceUrl('');
      await loadSongs();
      if (historyBySong[song.key]) loadHistory(song.key);
      toast.success(updatedCount > 0
        ? `Link saved — updated ${updatedCount} song${updatedCount !== 1 ? 's' : ''} in your upcoming events`
        : 'Link saved');
    } catch (err) {
      console.error('[MatchLibraryPage] Replacing the link failed:', err);
      toast.error(err instanceof Error && err.message ? err.message : 'Failed to save the link');
    } finally {
      setReplaceSaving(false);
    }
  }, [djUid, replaceUrl, loadSongs, historyBySong, loadHistory]);

  const handleMarkVerified = useCallback(async (song: LibrarySong, link: LibraryLink) => {
    try {
      const failure = firstFailedSave(await saveSongLinks([{
        songKey: song.key,
        link: link.url,
        integrity: {
          integrityStatus: 'verified',
          resolvedTitle: link.resolvedTitle ?? null,
          resolvedArtist: link.resolvedArtist ?? null,
          titleSimilarity: link.titleSimilarity ?? null,
          artistSimilarity: link.artistSimilarity ?? null,
          lastCheckedAt: link.lastCheckedAt ?? null,
          checkError: null,
        },
      }]));
      if (failure) {
        toast.error(failure);
        return;
      }
      await loadSongs();
      toast.success('Marked as verified');
    } catch (err) {
      console.error('[MatchLibraryPage] Marking verified failed:', err);
      toast.error('Failed to update status');
    }
  }, [loadSongs]);

  const handleRecheckIntegrity = useCallback(async (song: LibrarySong, provider: LinkProvider) => {
    setRecheckingLink(`${song.key}|${provider}`);
    try {
      const result = await checkAndPersistIntegrity(song, provider);
      await loadSongs();
      toast.success(
        result.integrityStatus === 'matched' ? 'Match confirmed' :
        result.integrityStatus === 'mismatch' ? 'Mismatch detected' :
        'Check failed — try again later'
      );
    } catch (err) {
      console.error('[MatchLibraryPage] Integrity check failed:', err);
      toast.error('Failed to check integrity');
    } finally {
      setRecheckingLink(null);
    }
  }, [loadSongs]);

  const handleToggleHistory = (song: LibrarySong, provider: LinkProvider) => {
    const key = `${song.key}|${provider}`;
    setFlagForm(null);
    if (openHistory === key) {
      setOpenHistory(null);
      return;
    }
    setOpenHistory(key);
    if (!historyBySong[song.key]) loadHistory(song.key);
  };

  const handleReinstate = useCallback(async (song: LibrarySong, provider: LinkProvider, entry: LibraryHistoryEntry) => {
    setHistoryAction(entry.id);
    try {
      await revertSongLink({ songKey: song.key, provider, historyId: entry.id });
      toast.success('Earlier link reinstated');
      await Promise.all([loadSongs(), loadHistory(song.key)]);
    } catch (err) {
      console.error('[MatchLibraryPage] Reinstating the link failed:', err);
      toast.error(errorMessage(err, 'Failed to reinstate the link'));
    } finally {
      setHistoryAction(null);
    }
  }, [loadSongs, loadHistory]);

  const handleFlag = useCallback(async (song: LibrarySong, provider: LinkProvider, entry: LibraryHistoryEntry, reason: string) => {
    setHistoryAction(entry.id);
    try {
      const trimmed = reason.trim();
      const { opened } = await flagSongLink({
        songKey: song.key,
        provider,
        trackId: entry.trackId,
        historyId: entry.id,
        ...(trimmed ? { reason: trimmed } : {}),
      });
      toast.success(opened ? 'Sent to the owner for review' : 'Already flagged');
      setFlagForm(null);
    } catch (err) {
      console.error('[MatchLibraryPage] Flagging the link failed:', err);
      toast.error(errorMessage(err, 'Failed to flag the link'));
    } finally {
      setHistoryAction(null);
    }
  }, []);

  const handleRemove = useCallback(async (song: LibrarySong) => {
    const confirmed = window.confirm(
      `Remove "${song.title}" from your library? Other DJs who saved it keep the link, and it can still be suggested to you.`,
    );
    if (!confirmed) return;
    setRemovingSong(song.key);
    try {
      await removeLibrarySongs([song.key]);
      setSongs(prev => prev.filter(s => s.key !== song.key));
      toast.success('Removed from your library');
    } catch (err) {
      console.error('[MatchLibraryPage] Removing the song failed:', err);
      toast.error('Failed to remove the song');
    } finally {
      setRemovingSong(null);
    }
  }, []);

  const handleBatchCheck = useCallback(async () => {
    if (batchChecking) {
      batchAbortRef.current?.abort();
      return;
    }

    // Gather links to check — only skip 'verified' (DJ explicitly confirmed)
    const toCheck: BatchCheckItem[] = [];
    for (const song of filteredSongs) {
      for (const [provider, link] of savedLinks(song)) {
        if (statusOf(link) !== 'verified') toCheck.push({ song, provider });
      }
    }

    if (toCheck.length === 0) {
      toast('All visible links have been checked');
      return;
    }

    setBatchChecking(true);
    const controller = new AbortController();
    batchAbortRef.current = controller;

    const result = await batchCheckIntegrity(
      toCheck,
      (checked, total, current) => setBatchProgress({ checked, total, current }),
      controller.signal,
    );

    setBatchChecking(false);
    batchAbortRef.current = null;
    await loadSongs();

    toast.success(`Checked ${result.checked}: ${result.mismatches} mismatch${result.mismatches !== 1 ? 'es' : ''}, ${result.errors} error${result.errors !== 1 ? 's' : ''}`);
  }, [batchChecking, filteredSongs, loadSongs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const renderHistoryActions = (song: LibrarySong, provider: LinkProvider, entry: LibraryHistoryEntry) => {
    if (entry.deletedByOwner) return null;
    const current = song.links[provider];
    const canReinstate = entry.trackId !== current?.trackId
      && REINSTATABLE.includes(entry.action)
      && !(song.blockedTrackIds[provider] ?? []).includes(entry.trackId);
    const busy = historyAction === entry.id;
    return (
      <>
        {canReinstate && (
          <button
            onClick={() => handleReinstate(song, provider, entry)}
            disabled={busy || editingRevoked}
            className="px-2 py-1 rounded text-xs bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50 flex items-center gap-1"
            title={editingRevoked ? REVOKED_HINT : 'Make this the current link again'}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
            Reinstate
          </button>
        )}
        <button
          onClick={() => setFlagForm(flagForm?.entryId === entry.id ? null : { entryId: entry.id, reason: '' })}
          disabled={busy}
          className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
          title="Flag for review — ask the owner to delete this link"
          aria-label="Flag for review"
        >
          <Flag className="w-3.5 h-3.5" />
        </button>
      </>
    );
  };

  const renderFlagForm = (song: LibrarySong, provider: LinkProvider, entries: LibraryHistoryEntry[]) => {
    const entry = flagForm ? entries.find(item => item.id === flagForm.entryId && item.provider === provider) : undefined;
    if (!flagForm || !entry) return null;
    return (
      <div className="mt-2 p-2.5 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-200 dark:border-red-800">
        <p className="text-xs font-medium text-red-700 dark:text-red-400 mb-1.5">
          Ask the owner to delete this link? (optional reason)
        </p>
        <textarea
          value={flagForm.reason}
          onChange={e => setFlagForm({ entryId: entry.id, reason: e.target.value.slice(0, MAX_FLAG_REASON) })}
          className="input text-xs w-full mb-2"
          rows={2}
          maxLength={MAX_FLAG_REASON}
          placeholder="What's wrong with it?"
        />
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => setFlagForm(null)}
            className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            Cancel
          </button>
          <button
            onClick={() => handleFlag(song, provider, entry, flagForm.reason)}
            disabled={historyAction === entry.id}
            className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
          >
            {historyAction === entry.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Flag className="w-3 h-3" />}
            Send to the owner
          </button>
        </div>
      </div>
    );
  };

  const renderLengths = (song: LibrarySong) => {
    const lengths = song.isrc ? songLengths.get(song.isrc) : undefined;
    if (!lengths || Object.keys(lengths).length < 2) return null;
    const sources: SongLengthSource[] = ['apple', 'spotify', 'tidal'];
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
        <Clock className="w-3 h-3" />
        Lengths: {sources.map(source => `${LENGTH_LABELS[source]} ${lengths[source] !== undefined ? formatLengthMs(lengths[source]!) : '—'}`).join(' · ')}
      </p>
    );
  };

  const renderReplaceForm = (song: LibrarySong, provider: LinkProvider, isNew: boolean) => (
    <div className="mt-2 p-2.5 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800">
      <p className="text-xs font-medium text-blue-700 dark:text-blue-400 mb-1.5">
        {isNew ? `Add a ${platformNameOf(provider)} link` : `Replace the ${platformNameOf(provider)} link`} — every DJ will use the new one
      </p>
      <input
        type="text"
        value={replaceUrl}
        onChange={e => setReplaceUrl(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') handleSaveReplace(song, provider); }}
        className="input text-xs w-full mb-2"
        placeholder={`Paste a ${platformNameOf(provider)} track link`}
        autoFocus
      />
      <div className="flex gap-2 justify-end">
        <button
          onClick={() => setEditingLink(null)}
          className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
        >
          Cancel
        </button>
        <button
          onClick={() => handleSaveReplace(song, provider)}
          disabled={replaceSaving || !replaceUrl.trim()}
          className="px-2 py-1 text-xs bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1"
        >
          {replaceSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          Save Link
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Match Library</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Songs you've matched across all events. {songs.length} song{songs.length !== 1 ? 's' : ''} saved.
        </p>
        <p className="text-xs text-gray-400 mt-1">Links are shared with every DJ. The latest saved link is used everywhere.</p>
      </div>

      {editingRevoked && (
        <div className="mb-4 p-3 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-800 dark:text-amber-300 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{REVOKED_HINT}</span>
        </div>
      )}

      {/* Search + Sort + Integrity Filter */}
      <div className="flex gap-3 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by title or artist..."
            className="input pl-9 w-full text-sm"
          />
        </div>
        <select
          value={sortField}
          onChange={e => setSortField(e.target.value as SortField)}
          className="input text-sm w-36"
        >
          <option value="recent">Recently Saved</option>
          <option value="title">Title</option>
          <option value="artist">Artist</option>
        </select>
        <select
          value={integrityFilter}
          onChange={e => setIntegrityFilter(e.target.value as IntegrityFilter)}
          className="input text-sm w-36"
        >
          <option value="all">All Links</option>
          <option value="mismatch">Mismatches</option>
          <option value="unchecked">Unchecked</option>
          <option value="matched">Confirmed</option>
          <option value="verified">Verified</option>
          <option value="error">Errors</option>
          <option value="reported">Reported</option>
        </select>
        {tagSummary.length > 0 && (
          <select
            value={tagFilter}
            onChange={e => setTagFilter(e.target.value)}
            className="input text-sm w-36"
            aria-label="Filter by tag"
          >
            <option value="all">All Tags</option>
            {tagSummary.map(({ tag, count }) => (
              <option key={tag} value={tag}>{tag} ({count})</option>
            ))}
          </select>
        )}
      </div>

      {/* Integrity stats bar + batch check */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          {integrityStats.mismatches > 0 && (
            <button
              onClick={() => setIntegrityFilter('mismatch')}
              className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 transition-colors ${
                integrityFilter === 'mismatch'
                  ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800'
              }`}
            >
              <AlertTriangle className="w-3 h-3" />
              {integrityStats.mismatches} mismatch{integrityStats.mismatches !== 1 ? 'es' : ''}
            </button>
          )}
          {integrityStats.unchecked > 0 && (
            <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 px-2 py-1 rounded-full">
              {integrityStats.unchecked} unchecked
            </span>
          )}
          {integrityStats.errors > 0 && (
            <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-2 py-1 rounded-full">
              {integrityStats.errors} error{integrityStats.errors !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button
          onClick={handleBatchCheck}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
        >
          {batchChecking ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Checking ({batchProgress.checked}/{batchProgress.total})... Cancel
            </>
          ) : (
            <>
              <ShieldCheck className="w-3.5 h-3.5" />
              Check Integrity
            </>
          )}
        </button>
      </div>

      {/* Batch check progress bar */}
      {batchChecking && (
        <div className="mb-4">
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
            <div
              className="bg-primary h-1.5 rounded-full transition-all"
              style={{ width: `${batchProgress.total > 0 ? (batchProgress.checked / batchProgress.total) * 100 : 0}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-1 truncate">{batchProgress.current}</p>
        </div>
      )}

      {/* Player versions Bridge learned from off-track picks (ITER-181) */}
      {bridgeAliases.length > 0 && (
        <div className="mb-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" data-testid="bridge-aliases">
          <button
            type="button"
            onClick={() => setAliasesOpen(open => !open)}
            className="w-full flex items-center justify-between gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 text-left transition-colors"
          >
            <span className="font-medium text-gray-900 dark:text-white text-sm">
              Versions Bridge has learned ({bridgeAliases.length})
            </span>
            {aliasesOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {aliasesOpen && (
            <div className="px-3 pb-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                When you pick a song Bridge didn&rsquo;t recognize, Bridge remembers that version for next time. Remove one if it was the wrong pick.
              </p>
              <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                {bridgeAliases.map(alias => (
                  <li key={alias.aliasId} className="flex items-center gap-3 py-2">
                    <span className="flex-1 min-w-0 text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-semibold">{alias.heardTitle} – {alias.heardArtist}</span>
                      {' → '}
                      {alias.title} – {alias.artist}
                    </span>
                    <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 px-2 py-0.5 rounded-full flex-shrink-0">
                      {platformNameOf(alias.provider)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveBridgeAlias(alias.aliasId)}
                      disabled={removingAlias === alias.aliasId}
                      className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:text-red-700 flex-shrink-0 disabled:opacity-50"
                    >
                      {removingAlias === alias.aliasId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Song list */}
      {filteredSongs.length === 0 ? (
        <div className="text-center py-12">
          <Music className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">
            {tagFilter !== 'all' ? 'No songs with this tag.' : searchQuery ? 'No matches found for your search.' : 'No saved matches yet. Matches are saved automatically when you approve songs.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredSongs.map(song => {
            const isExpanded = expandedSong === song.key;
            const links = savedLinks(song);
            const artUrl = links.find(([, link]) => link.albumArtUrl)?.[1].albumArtUrl;
            const status = songStatus(song);
            const missingProviders = PROVIDERS.filter(provider => !song.links[provider]);
            const songLengthsKnown = song.isrc ? songLengths.get(song.isrc) : undefined;
            const differentVersion = songLengthsKnown ? bigLengthGaps(songLengthsKnown).length > 0 : false;
            const songTags = tagsBySong.get(song.key) ?? [];

            return (
              <div key={song.key} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                {/* Song header */}
                <button
                  onClick={() => handleToggleExpand(song.key)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 text-left transition-colors"
                >
                  {artUrl ? (
                    <img src={artUrl} alt="" className="w-10 h-10 rounded flex-shrink-0 object-cover" />
                  ) : (
                    <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded flex-shrink-0 flex items-center justify-center">
                      <Music className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">{song.title}</p>
                    <p className="text-sm text-gray-500 truncate">{song.artist}</p>
                    {songTags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {songTags.slice(0, 3).map(tag => (
                          <span key={tag} className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full">
                            {tag}
                          </span>
                        ))}
                        {songTags.length > 3 && (
                          <span className="text-xs text-gray-500 px-1 py-0.5">+{songTags.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                    {differentVersion && (
                      <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full" title="The catalogs disagree on this song's length by a lot">
                        Likely a different version
                      </span>
                    )}
                    {/* Integrity status badge */}
                    {links.length > 0 && status === 'mismatch' && (
                      <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full flex items-center gap-0.5" title="URL metadata doesn't match song data">
                        <AlertTriangle className="w-3 h-3" /> Mismatch
                      </span>
                    )}
                    {links.length > 0 && status === 'error' && (
                      <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full flex items-center gap-0.5" title="Check failed">
                        <AlertTriangle className="w-3 h-3" /> Error
                      </span>
                    )}
                    {links.length > 0 && status === 'verified' && (
                      <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full flex items-center gap-0.5" title="Manually verified">
                        <CheckCircle className="w-3 h-3" /> Verified
                      </span>
                    )}
                    {links.length > 0 && status === 'matched' && (
                      <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full flex items-center gap-0.5" title="URL confirmed to match song data">
                        <CheckCircle className="w-3 h-3" />
                      </span>
                    )}
                    {links.map(([provider, link]) => (
                      <span key={provider} className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">
                        {link.platformName}
                      </span>
                    ))}
                    {links.length === 0 && (
                      <span className="text-xs text-gray-400">No current link</span>
                    )}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </button>

                {/* Expanded link list */}
                {isExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30 p-3 space-y-2">
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Tags</p>
                      <SongTagEditor
                        tags={songTags}
                        allTags={allTags}
                        saving={savingTagsFor === song.key}
                        onChange={next => handleTagsChange(song.key, next)}
                      />
                    </div>
                    {renderLengths(song)}
                    {links.map(([provider, link]) => {
                      const linkKey = `${song.key}|${provider}`;
                      const historyEntries = historyBySong[song.key] ?? [];
                      return (
                        <div key={provider} className="p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                          <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="text-sm font-medium text-gray-900 dark:text-white">{link.platformName}</span>
                                {link.durationMs && (
                                  <span className="text-xs text-gray-400 flex items-center gap-0.5">
                                    <Clock className="w-3 h-3" /> {formatDurationMmSs(link.durationMs)}
                                  </span>
                                )}
                                {link.wrongSongReports > 0 && (
                                  <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full" title="A DJ reported this link as the wrong song">
                                    Reported
                                  </span>
                                )}
                                {flagFor(recognitions.get(song.key), provider) && (
                                  <span
                                    className="text-xs bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 px-2 py-0.5 rounded-full flex items-center gap-1"
                                    title="Bridge has missed this song in this player. The queue warns you when it comes up."
                                  >
                                    <EyeOff className="w-3 h-3" />
                                    Bridge didn&rsquo;t recognize ({platformNameOf(provider)})
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveRecognitionFlag(song.key, provider)}
                                      disabled={unflagging === `${song.key}|${provider}`}
                                      className="underline font-medium ml-1 disabled:opacity-50"
                                    >
                                      Remove flag
                                    </button>
                                  </span>
                                )}
                                {song.linkStats[provider]?.neverCache && (
                                  <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full" title="This link keeps changing. It's skipped by auto-match until it has 3 clean uses.">
                                    Never reused
                                  </span>
                                )}
                              </div>
                              {link.note && (
                                <p className="text-xs text-gray-500 italic mb-1">{link.note}</p>
                              )}
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline truncate block"
                                title={link.url}
                              >
                                <Link2 className="w-3 h-3 inline mr-1" />
                                {link.url}
                              </a>
                              <p className="text-xs text-gray-400 mt-1">
                                {link.setBy === djUid ? 'Saved by you' : 'Saved by another DJ'}
                                {formatSavedDate(link.setAt) ? ` · ${formatSavedDate(link.setAt)}` : ''}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{confidenceText(link)}</p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={() => handleRecheckIntegrity(song, provider)}
                                disabled={recheckingLink === linkKey}
                                className="p-1.5 rounded-lg hover:bg-primary/10 text-gray-400 hover:text-primary disabled:opacity-50"
                                title="Check link"
                              >
                                {recheckingLink === linkKey
                                  ? <Loader2 className="w-4 h-4 animate-spin" />
                                  : <RefreshCw className="w-4 h-4" />
                                }
                              </button>
                              <button
                                onClick={() => handleToggleHistory(song, provider)}
                                className={`p-1.5 rounded-lg hover:bg-primary/10 hover:text-primary ${openHistory === linkKey ? 'text-primary' : 'text-gray-400'}`}
                                title="History"
                              >
                                {historyLoading === song.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <History className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => handleStartReplace(song, provider)}
                                disabled={editingRevoked}
                                className="p-1.5 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 text-gray-400 hover:text-blue-600 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                                title={editingRevoked ? REVOKED_HINT : 'Replace link'}
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {editingLink === linkKey && renderReplaceForm(song, provider, false)}

                          {openHistory === linkKey && (
                            <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                              <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Link history</p>
                              {historyLoading === song.key && historyEntries.length === 0 ? (
                                <p className="text-xs text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading...</p>
                              ) : (
                                <SongLinkHistoryList
                                  song={song}
                                  provider={provider}
                                  entries={historyEntries}
                                  currentDjUid={djUid}
                                  renderActions={entry => renderHistoryActions(song, provider, entry)}
                                />
                              )}
                              {renderFlagForm(song, provider, historyEntries)}
                            </div>
                          )}

                          {/* Integrity mismatch details + resolution actions */}
                          {link.integrityStatus === 'mismatch' && (
                            <div className="mt-2 p-2.5 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-200 dark:border-red-800 text-xs">
                              <p className="font-medium text-red-700 dark:text-red-400 mb-1.5 flex items-center gap-1">
                                <AlertTriangle className="w-3.5 h-3.5" /> URL points to different song
                              </p>
                              <div className="text-red-600 dark:text-red-300 space-y-0.5">
                                <p>Stored: <span className="font-medium">"{song.title}"</span> by {song.artist}</p>
                                <p>URL: <span className="font-medium">"{link.resolvedTitle}"</span> by {link.resolvedArtist}</p>
                              </div>
                              <p className="text-red-500 dark:text-red-400 mt-1">
                                Title: {Math.round((link.titleSimilarity || 0) * 100)}% match &middot; Artist: {Math.round((link.artistSimilarity || 0) * 100)}% match
                              </p>
                              <div className="flex gap-2 mt-2 flex-wrap">
                                <button
                                  onClick={() => openSearch(searchUrlFor(song.title, song.artist, playModeConfig(MODE_BY_PROVIDER[provider])))}
                                  className="px-2 py-1 bg-primary text-white rounded text-xs hover:bg-primary/90 transition-colors flex items-center gap-1"
                                >
                                  <ExternalLink className="w-3 h-3" /> Search in {link.platformName}
                                </button>
                                <button
                                  onClick={() => handleStartReplace(song, provider)}
                                  disabled={editingRevoked}
                                  className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                                  title={editingRevoked ? REVOKED_HINT : undefined}
                                >
                                  Replace Link
                                </button>
                                <button
                                  onClick={() => handleMarkVerified(song, link)}
                                  className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600 transition-colors"
                                >
                                  Mark Verified
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Integrity error details */}
                          {link.integrityStatus === 'error' && (
                            <div className="mt-2 p-2 bg-orange-50 dark:bg-orange-900/10 rounded-lg border border-orange-200 dark:border-orange-800 text-xs">
                              <p className="text-orange-600 dark:text-orange-400 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" /> Check failed: {link.checkError || 'Unknown error'}
                              </p>
                            </div>
                          )}

                          {/* Verified indicator (subtle) */}
                          {link.integrityStatus === 'verified' && (
                            <div className="mt-1.5 flex items-center gap-1 text-xs text-green-600 dark:text-green-400 opacity-70">
                              <CheckCircle className="w-3 h-3" /> Manually verified
                              {link.resolvedTitle && link.resolvedTitle !== song.title && (
                                <span className="text-gray-400 ml-1" title={`URL resolves to: "${link.resolvedTitle}" by ${link.resolvedArtist}`}>
                                  (URL differs — verified OK)
                                </span>
                              )}
                            </div>
                          )}

                          {/* Matched indicator (subtle) */}
                          {link.integrityStatus === 'matched' && link.lastCheckedAt && (
                            <div className="mt-1.5 flex items-center gap-1 text-xs text-green-600 dark:text-green-400 opacity-50">
                              <CheckCircle className="w-3 h-3" /> Confirmed match
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Add a link for a player the song doesn't have yet */}
                    {missingProviders.map(provider => (
                      editingLink === `${song.key}|${provider}` ? (
                        <div key={provider}>{renderReplaceForm(song, provider, true)}</div>
                      ) : (
                        <button
                          key={provider}
                          onClick={() => handleStartReplace(song, provider)}
                          className="w-full flex items-center justify-center gap-1.5 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 hover:text-primary hover:border-primary transition-colors"
                        >
                          <Plus className="w-4 h-4" /> Add {platformNameOf(provider)} link
                        </button>
                      )
                    ))}

                    <div className="flex justify-end pt-1">
                      <button
                        onClick={() => handleRemove(song)}
                        disabled={removingSong === song.key}
                        className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-50"
                      >
                        {removingSong === song.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        Remove from my library
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
